import { DEFAULT_LOCALE } from '@featit/shared/i18n/core';
import { failures } from '@featit/shared/i18n/messages/en/failures';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { roleRefusalOf, type IRoleRefusalFacts } from '@featit/shared/role-refusal';
import { seededRoleWordsIn } from '@/features/settings/seededRoleNames';
import { registerEnglishArea } from '@/i18n/catalogue';
import { englishT, type TFn } from '@/i18n/LocaleProvider';
import { pageLocale, pageT } from '@/i18n/pageTranslator';

registerEnglishArea('failures', failures);

/**
 * I-2195: a refusal that names a role, worded here from its code and the role's facts
 * (`failures.roleRefusal.*`), so a German reader is not told "You cannot grant Work Area Viewer" and a
 * package's "Producer" is "Produzent". The server keeps its English sentence for the CLI (D-0212); this
 * is the screen's own (F-0324).
 */
export interface IRoleRefusalReason {
  key: MessageKey;
  facts: IRoleRefusalFacts;
}

/**
 * The object that carries the code: a REST body, or a GraphQL error's extensions or original error.
 * I-2439's coded refusals are read from the same places (`readerRefusalReason`).
 */
export function refusalBodies(error: unknown): unknown[] {
  const candidate = error as {
    response?: { data?: unknown };
    extensions?: { originalError?: unknown } & Record<string, unknown>;
  } | null;
  return [candidate?.response?.data, candidate?.extensions?.originalError, candidate?.extensions];
}

const INSTALL_KEYS: ReadonlySet<string> = new Set([
  'scopeMismatch',
  'neverGranted',
  'noRoleAssignGrant',
  'aboveOwnRoleGrant',
]);

/** Null for any failure that is not a role refusal (or one from a server that sent no facts). */
export function roleRefusalReason(error: unknown): IRoleRefusalReason | null {
  for (const body of refusalBodies(error)) {
    const refusal = roleRefusalOf(body);
    if (!refusal) continue;
    const remove = refusal.facts.verb === 'remove';
    const verbal =
      refusal.key === 'noRoleAssign' ||
      refusal.key === 'aboveOwnRole' ||
      refusal.key === 'permissionsNotHeld';
    const verbName = verbal ? `${refusal.key}${remove ? 'Remove' : 'Grant'}` : refusal.key;
    // While a package was being installed, the sentence also says who to ask.
    const name =
      refusal.facts.during === 'install' && INSTALL_KEYS.has(verbName)
        ? `${verbName}Install`
        : verbName;
    return { key: `failures.roleRefusal.${name}` as MessageKey, facts: refusal.facts };
  }
  return null;
}

/**
 * The role's name in `t`'s language: the name a package installed in the work area gives it, else the
 * seeded words of a role still under its seeded name, else the name as stored. As the other role
 * screens read it (`seededRoleWords`).
 */
export function refusedRoleIn(t: TFn, facts: IRoleRefusalFacts): string {
  const { en, de } = facts.roleNames;
  const locale = pageLocale(DEFAULT_LOCALE);
  if (de && locale === 'de') return de;
  // In English a seeded role under its seeded name reads as that name, so the launch entry, which
  // reaches this module, needs no `seededRoles` area (F-0621).
  if (locale === DEFAULT_LOCALE) return en;
  return seededRoleWordsIn(t, { key: facts.roleKey, name: en });
}

/** The arguments of the sentence, with the role as the English reads it. */
export function roleRefusalArgs(facts: IRoleRefusalFacts, role: string): Record<string, string> {
  return { role, permission: facts.permission ?? '' };
}

/**
 * The sentence a role refusal says in the page's language, for `ApiService.httpErrorMessage`, which has
 * no translator of its own. Mirrors `verificationReadOnlySentence` (I-2189 siblings) and
 * `planRefusalSentence` (I-1712) rather than sharing their code: the three read the same bodies for
 * different codes. Null for any failure that is not a role refusal.
 */
export function roleRefusalSentence(error: unknown): string | null {
  const reason = roleRefusalReason(error);
  if (!reason) return null;
  const t = pageT(englishT);
  return t(reason.key, roleRefusalArgs(reason.facts, refusedRoleIn(t, reason.facts)));
}
